<template>
  <SplitPanelShell
    title="Drafts & Captured"
    icon="bi bi-file-earmark-text"
    :count="records.length || null"
    :toolbar-columns="2"
    layout="single"
  >
    <template #actions>
      <SecondaryButton
        icon="bi bi-arrow-clockwise"
        label=""
        custom-class="split-panel-shell__icon-action"
        :loading="loading"
        :disabled="loading"
        title="Refresh"
        @click="fetchData"
      />
    </template>

    <template #stats>
      <template v-if="records && records.length > 0">
        <div class="split-panel-shell__stat">
          <span class="split-panel-shell__stat-label">Total</span>
          <span class="split-panel-shell__stat-value">{{ formatCurrency(totalAmount) }}</span>
        </div>
        <div class="split-panel-shell__stat-divider"></div>
        <div class="split-panel-shell__stat">
          <span class="split-panel-shell__stat-label">Bills</span>
          <span class="split-panel-shell__stat-value">{{ records.length }}</span>
        </div>
      </template>
    </template>

    <template #toolbar>
      <LabelSearchField
        input-id="draft-bills-search"
        v-model="globalSearchQuery"
        label="Search"
        placeholder="Search vendor, amount, bill no..."
        flex
      />
      <LabelDropdown
        label="Vendor"
        select-id="draft-bills-vendor"
        v-model="selectedVendorId"
        inline
        flex
        menu-class="vendor-bills-expenses-dropdown-menu"
        :options="vendorFilterOptions"
        @change="fetchData"
      />
    </template>

    <template #content>
      <div v-if="loading && !(records && records.length)" class="split-panel-shell__empty">
        <i class="bi bi-arrow-repeat spinning" aria-hidden="true"></i>
        <p>Loading draft bills...</p>
      </div>

      <div v-else-if="!(records && records.length)" class="split-panel-shell__empty">
        <div class="split-panel-shell__empty-icon">
          <i class="bi bi-file-earmark-plus" aria-hidden="true"></i>
        </div>
        <h4>No draft bills</h4>
        <p>Invoice and manual drafts appear here. Portal submissions stay in Invites &amp; Notes.</p>
      </div>

      <div v-else class="draft-bills-panel-content drafts-panel">
        <div class="drafts-panel__selectbar">
          <label class="drafts-panel__select-all">
            <input
              type="checkbox"
              class="draft-card__checkbox"
              :checked="allPageSelected"
              :indeterminate.prop="somePageSelected && !allPageSelected"
              aria-label="Select all bills on this page"
              @change="onSelectAllPage"
            />
            <span>Select bills</span>
          </label>
          <span v-if="selectedCount" class="drafts-panel__selected-count">{{ selectedCount }} selected</span>
          <button
            v-if="selectedCount"
            type="button"
            class="drafts-panel__clear"
            @click="clearSelection"
          >
            Clear
          </button>
        </div>
        <div class="split-panel-shell__list-scroll draft-bills-card-scroll drafts-panel__scroll">
          <div class="drafts-panel__grid">
            <article
              v-for="record in (paginatedRecords || [])"
              :key="'draft-bill-' + record.id"
              class="draft-card"
              :class="{ 'draft-card--selected': isSelectedRecord(record) }"
              role="button"
              tabindex="0"
              :aria-pressed="isSelectedRecord(record) ? 'true' : 'false'"
              @click="selectCard(record)"
              @keydown.enter.prevent="selectCard(record)"
              @keydown.space.prevent="selectCard(record)"
            >
              <header class="draft-card__header">
                <label class="draft-card__check" @click.stop @keydown.stop>
                  <input
                    type="checkbox"
                    class="draft-card__checkbox"
                    :checked="isCheckedRecord(record)"
                    :aria-label="'Select ' + (record.vendorName || 'bill')"
                    @change="toggleBill(record, $event.target.checked)"
                  />
                </label>
                <span
                  class="draft-card__avatar"
                  :class="{ 'draft-card__avatar--image': Boolean(vendorLogoForRecord(record)) }"
                  aria-hidden="true"
                >
                  <img
                    v-if="vendorLogoForRecord(record)"
                    :src="vendorLogoForRecord(record)"
                    alt=""
                    class="draft-card__avatar-img"
                    @error="onVendorLogoError(record)"
                  >
                  <span v-else class="draft-card__avatar-initials">
                    {{ vendorInitials(record.vendorName) }}
                  </span>
                </span>
                <div class="draft-card__title-block">
                  <div class="draft-card__title-row">
                    <h4 class="draft-card__vendor">{{ record.vendorName || 'Unknown vendor' }}</h4>
                    <span
                      v-if="isCapturedBill(record)"
                      class="draft-card__badge draft-card__badge--captured"
                    >Captured</span>
                    <span
                      v-else
                      class="draft-card__badge"
                    >Draft</span>
                  </div>
                  <p class="draft-card__bill-no">{{ record.no || '—' }}</p>
                </div>
                <div class="draft-card__amount-block">
                  <strong class="draft-card__amount">{{ formatCurrency(record.originalAmount) }}</strong>
                  <time class="draft-card__date">{{ formatDate(record.date) }}</time>
                </div>
              </header>

              <dl class="draft-card__facts" aria-label="Bill details">
                <div class="draft-card__fact draft-card__fact--full">
                  <dt>Consultant</dt>
                  <dd>{{ consultantNamesLabel(record) }}</dd>
                </div>

                <div class="draft-card__periods">
                  <div class="draft-card__periods-head">
                    <span>Period</span>
                    <span>Hours</span>
                  </div>
                  <div
                    v-for="(row, idx) in periodHoursRows(record)"
                    :key="'period-' + record.id + '-' + idx"
                    class="draft-card__period-row"
                  >
                    <span class="draft-card__period-dates">{{ row.period }}</span>
                    <span class="draft-card__period-hours">{{ row.hoursLabel }}</span>
                  </div>
                  <div class="draft-card__period-row draft-card__period-row--total">
                    <span class="draft-card__period-dates">Total hours</span>
                    <span class="draft-card__period-hours">{{ totalHoursLabel(record) }}</span>
                  </div>
                </div>

                <div class="draft-card__fact-grid">
                  <div class="draft-card__fact">
                    <dt>Rate</dt>
                    <dd>{{ rateLabel(record) }}</dd>
                  </div>
                  <div class="draft-card__fact">
                    <dt>Payment terms</dt>
                    <dd>{{ paymentTermsLabel(record) }}</dd>
                  </div>
                  <div class="draft-card__fact">
                    <dt>Due date</dt>
                    <dd>{{ formatDate(record.dueDate) || '—' }}</dd>
                  </div>
                  <div class="draft-card__fact">
                    <dt>Entries</dt>
                    <dd>{{ consultantLabel(record) }}</dd>
                  </div>
                </div>

                <div class="draft-card__fact draft-card__fact--full draft-card__fact--email">
                  <dt>Email</dt>
                  <dd :title="vendorEmailFor(record) || 'No vendor email'">
                    {{ vendorEmailFor(record) || 'No vendor email' }}
                  </dd>
                </div>
              </dl>

              <div class="draft-card__actions" @click.stop>
                <PrimaryButton
                  icon="bi bi-box-arrow-up-right"
                  label="Open"
                  compact
                  @click="openBill(record)"
                />
              </div>
            </article>
          </div>
        </div>

        <div v-if="records.length > itemsPerPage" class="split-panel-shell__pagination">
          <PaginationComponent
            :current-page="currentPage"
            :total-items="records.length"
            :items-per-page="itemsPerPage"
            item-label="bills"
            :show-items-per-page="false"
            @page-change="currentPage = $event"
          />
        </div>
      </div>
    </template>
  </SplitPanelShell>
</template>

<script>
import logger from '@/utils/logger';
import ApiService from '@/services/api.service';
import LabelSearchField from '@/modules/consultant/components/LabelSearchField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import SplitPanelShell from '@/modules/vendor/components/SplitPanelShell.vue';
import {
  ensureRecordDetails,
  formatRecordStatus,
} from '@/modules/vendor/utils/billsExpensesDetailService';
import { formatPanelCurrency, formatUsDate, formatUsPeriodText } from '@/modules/vendor/utils/splitPanelUtils';

export default {
  name: 'DraftBillsModernPanel',
  components: {
    LabelSearchField,
    LabelDropdown,
    SecondaryButton,
    PrimaryButton,
    PaginationComponent,
    SplitPanelShell,
  },
  props: {
    capturedBillIds: {
      type: Array,
      default: () => [],
    },
    selectedBillId: {
      default: null,
    },
    selectedBillIds: {
      type: Array,
      default: () => [],
    },
    persistSelection: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select-bill', 'select-bills'],
  data() {
    return {
      selectedVendorId: 'All',
      globalSearchQuery: '',
      vendors: [],
      paymentTerms: [],
      allRecords: [],
      loading: false,
      currentPage: 1,
      itemsPerPage: 50,
      /** vendorId -> blob URL, or null when no logo / load failed */
      vendorLogoUrls: {},
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id || this.$route.params.companyId;
    },
    vendorFilterOptions() {
      return [
        { value: 'All', label: 'All vendors' },
        ...(this.vendors || []).map((vendor) => ({
          value: String(vendor.id),
          label: vendor.vendorName || vendor.name || 'Vendor',
        })),
      ];
    },
    vendorEmailById() {
      const map = Object.create(null);
      (this.vendors || []).forEach((vendor) => {
        if (vendor?.id == null) return;
        const email = String(vendor.email || '').trim();
        if (email) map[String(vendor.id)] = email;
      });
      return map;
    },
    records() {
      const list = Array.isArray(this.allRecords) ? this.allRecords : [];
      const drafts = list.filter(
        (r) => r && r.type === 'Bill' && this.isDraftBillRecord(r)
      );
      const filtered = this.filterRecordsBySearch(drafts, this.globalSearchQuery);
      const rows = Array.isArray(filtered) ? filtered.slice() : [];
      // Mail-captured drafts first (Draft Bills = Draft vs Captured)
      rows.sort((a, b) => {
        const aCaptured = this.isCapturedBill(a) ? 0 : 1;
        const bCaptured = this.isCapturedBill(b) ? 0 : 1;
        return aCaptured - bCaptured;
      });
      return rows;
    },
    paginatedRecords() {
      const list = Array.isArray(this.records) ? this.records : [];
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return list.slice(start, start + this.itemsPerPage);
    },
    totalAmount() {
      const list = Array.isArray(this.records) ? this.records : [];
      return list.reduce(
        (sum, record) => sum + (parseFloat(record.originalAmount) || 0),
        0
      );
    },
    capturedBillIdSet() {
      const ids = Array.isArray(this.capturedBillIds) ? this.capturedBillIds : [];
      return new Set(ids.map((id) => String(id)));
    },
    selectedCount() {
      return (this.selectedBillIds || []).length;
    },
    pageBillIds() {
      return (this.paginatedRecords || [])
        .map((record) => record?.id)
        .filter((id) => id != null);
    },
    allPageSelected() {
      const ids = this.pageBillIds;
      if (!ids.length) return false;
      return ids.every((id) => (this.selectedBillIds || []).some((selected) => String(selected) === String(id)));
    },
    somePageSelected() {
      return this.pageBillIds.some((id) =>
        (this.selectedBillIds || []).some((selected) => String(selected) === String(id))
      );
    },
  },
  watch: {
    globalSearchQuery() {
      this.currentPage = 1;
    },
    paginatedRecords: {
      immediate: true,
      handler(list) {
        this.loadDetailsForRecords(list);
        this.prefetchVendorLogosForRecords(list);
      },
    },
  },
  async mounted() {
    await this.fetchVendors();
    await this.fetchData();
  },
  beforeUnmount() {
    this.revokeAllVendorLogoUrls();
  },
  methods: {
    isCapturedBill(record) {
      if (record?.id == null) return false;
      return this.capturedBillIdSet.has(String(record.id));
    },
    isSelectedRecord(record) {
      if (this.isCheckedRecord(record)) return true;
      if (record?.id == null || this.selectedBillId == null || this.selectedBillId === '') return false;
      return String(this.selectedBillId) === String(record.id);
    },
    isCheckedRecord(record) {
      if (record?.id == null) return false;
      return (this.selectedBillIds || []).some((id) => String(id) === String(record.id));
    },
    isDraftBillRecord(record) {
      if (!record || record.type !== 'Bill') return false;
      if (String(record.status || '').trim().toLowerCase() !== 'draft') return false;
      // Portal Submitted → Invites & Notes (Draft vs Submitted). Draft Bills = Draft vs Captured only.
      if (record.isVendorSubmitted === true) return false;
      if (record.isVendorPortalDraft) return false;
      // Timesheet already portal-owned — compare / create from Invites & Notes, not Drafts.
      if (record.hasVendorInvoiceTimesheet === true) return false;
      return true;
    },
    vendorEmailFor(record) {
      if (!record) return '';
      if (record.vendorEmail) return String(record.vendorEmail).trim();
      if (record.email) return String(record.email).trim();
      const vendorId = record.vendorId != null ? String(record.vendorId) : '';
      return vendorId ? this.vendorEmailById[vendorId] || '' : '';
    },
    filterRecordsBySearch(records, query) {
      const list = Array.isArray(records) ? records : [];
      const search = String(query || '').trim().toLowerCase();
      if (!search) return list;
      return list.filter((record) => {
        const amountText = this.formatCurrency(record.originalAmount);
        const balanceText = this.formatCurrency(record.balance);
        const consultantNames = this.consultantRecordsFor(record)
          .map((c) => c.consultantName || c.name)
          .join(' ');
        const fields = [
          record.no,
          record.vendorName,
          this.vendorEmailFor(record),
          record.status,
          formatRecordStatus(record),
          record.originalAmount,
          record.balance,
          amountText,
          balanceText,
          consultantNames,
          this.periodLabel(record),
          this.rateLabel(record),
          this.formatDate(record.date),
        ];
        return fields.some((value) => String(value ?? '').toLowerCase().includes(search));
      });
    },
    consultantRecordsFor(record) {
      const fromDetails = record?.details?.consultantRecords;
      if (Array.isArray(fromDetails) && fromDetails.length) return fromDetails;
      if (Array.isArray(record?.consultantRecords) && record.consultantRecords.length) {
        return record.consultantRecords;
      }
      return [];
    },
    emitSelection(ids, activeId) {
      const next = Array.isArray(ids) ? ids : [];
      this.$emit('select-bills', next);
      if (activeId !== undefined) this.$emit('select-bill', activeId);
    },
    selectCard(record) {
      if (!record) return;
      const current = [...(this.selectedBillIds || [])];
      const already = current.some((id) => String(id) === String(record.id));
      if (!already) current.push(record.id);
      if (this.persistSelection) {
        const active =
          this.selectedBillId != null && this.selectedBillId !== ''
            ? this.selectedBillId
            : record.id;
        this.emitSelection(current, active);
        return;
      }
      const next = String(this.selectedBillId) === String(record.id) ? null : record.id;
      this.$emit('select-bill', next);
    },
    toggleBill(record, checked) {
      if (!record?.id) return;
      const current = (this.selectedBillIds || []).filter((id) => String(id) !== String(record.id));
      if (checked) current.push(record.id);
      let activeId = this.selectedBillId;
      if (checked) {
        if (activeId == null || activeId === '') activeId = record.id;
      } else if (String(this.selectedBillId) === String(record.id)) {
        activeId = current[0] ?? null;
      }
      this.emitSelection(current, activeId);
    },
    onSelectAllPage(event) {
      const pageIds = this.pageBillIds;
      const pageSet = new Set(pageIds.map((id) => String(id)));
      const kept = (this.selectedBillIds || []).filter((id) => !pageSet.has(String(id)));
      const next = event.target.checked ? kept.concat(pageIds) : kept;
      let activeId = this.selectedBillId;
      if (event.target.checked) {
        if (activeId == null || activeId === '' || !next.some((id) => String(id) === String(activeId))) {
          activeId = next[0] ?? null;
        }
      } else if (pageSet.has(String(activeId))) {
        activeId = next[0] ?? null;
      }
      this.emitSelection(next, activeId);
    },
    clearSelection() {
      this.emitSelection([], null);
    },
    openBill(record) {
      if (!record?.id || !this.companyId) return;
      this.$emit('select-bill', record.id);
      this.$router.push({
        name: 'CreateBill',
        params: { id: this.companyId },
        query: {
          billId: String(record.id),
          vendorId: record.vendorId != null ? String(record.vendorId) : undefined,
        },
      });
    },
    consultantCount(record) {
      if (record?.loadingDetails && !this.consultantRecordsFor(record).length) return null;
      if (record?.consultantCount != null && !this.consultantRecordsFor(record).length) {
        return Number(record.consultantCount) || 0;
      }
      if (!record?.details && !record?.consultantRecords) return null;
      return this.consultantRecordsFor(record).length;
    },
    consultantLabel(record) {
      const count = this.consultantCount(record);
      if (count == null) return '…';
      return `${count} entr${count === 1 ? 'y' : 'ies'}`;
    },
    vendorRecordFor(record) {
      const id = record?.vendorId;
      if (id == null) return null;
      return (this.vendors || []).find((v) => String(v.id) === String(id)) || null;
    },
    paymentTermRecord(record) {
      const vendor = this.vendorRecordFor(record);
      const termId =
        record?.paymentTermId
        || record?.paymentTerm
        || vendor?.paymentTermId
        || vendor?.paymentTerm?.id
        || null;
      if (termId == null || termId === '') return null;
      return (this.paymentTerms || []).find((t) => String(t.id) === String(termId)) || null;
    },
    paymentTermsLabel(record) {
      if (!record) return '—';
      const term = this.paymentTermRecord(record);
      const vendor = this.vendorRecordFor(record);
      const name = String(
        record.paymentTermName
        || record.paymentTermLabel
        || term?.name
        || vendor?.paymentTermName
        || vendor?.paymentTerm?.name
        || ''
      ).trim();
      if (name) return name;
      const dueDays = record.paymentTermDueDays != null
        ? Number(record.paymentTermDueDays)
        : (term?.dueDays != null ? Number(term.dueDays) : null);
      if (dueDays != null && Number.isFinite(dueDays)) {
        return `Net ${dueDays}`;
      }
      return '—';
    },
    consultantHours(record) {
      if (record?.loadingDetails && !this.consultantRecordsFor(record).length) return '…';
      const rows = this.consultantRecordsFor(record);
      if (rows.length) {
        const total = rows.reduce((sum, row) => sum + (parseFloat(row.hours) || 0), 0);
        return Number.isInteger(total) ? total : total.toFixed(2);
      }
      if (record?.consultantHoursTotal != null) {
        const total = Number(record.consultantHoursTotal) || 0;
        return Number.isInteger(total) ? total : total.toFixed(2);
      }
      if (!record?.details && !record?.consultantRecords) return '—';
      return '0';
    },
    periodFromRow(row) {
      if (!row) return '';
      const from = formatUsDate(row.fromDate || row.from_date || row.periodStart || '');
      const to = formatUsDate(row.toDate || row.to_date || row.periodEnd || '');
      if (from && to && from !== to) return `${from} – ${to}`;
      if (from && to) return from;
      if (from || to) return from || to;
      const raw = String(row.description || '').trim();
      if (!raw) return '';
      const cleaned = formatUsPeriodText(raw)
        .replace(/\s*\(\s*[\d.]+\s*hrs?\s*\)\s*/gi, '')
        .replace(/\s+to\s+/gi, ' – ')
        .replace(/\s*[–—-]\s*/g, ' – ')
        .trim();
      if (!/\d{1,2}\/\d{1,2}\/\d{2,4}/.test(cleaned)) return '';
      return cleaned || '';
    },
    formatHoursValue(value) {
      const hours = Number(value) || 0;
      return Number.isInteger(hours) ? String(hours) : hours.toFixed(2);
    },
    totalHoursLabel(record) {
      if (record?.loadingDetails && !this.consultantRecordsFor(record).length) return '…';
      return `${this.consultantHours(record)} hrs`;
    },
    periodHoursRows(record) {
      if (record?.loadingDetails && !this.consultantRecordsFor(record).length) {
        return [{ period: '…', hoursLabel: '…' }];
      }
      const rows = this.consultantRecordsFor(record);
      if (rows.length) {
        const byPeriod = new Map();
        rows.forEach((row) => {
          const period = this.periodFromRow(row) || 'Not set';
          const key = period.toLowerCase();
          const hours = parseFloat(row.hours) || 0;
          if (!byPeriod.has(key)) {
            byPeriod.set(key, { period, hours: 0 });
          }
          byPeriod.get(key).hours += hours;
        });
        return [...byPeriod.values()].map((item) => ({
          period: item.period,
          hoursLabel: `${this.formatHoursValue(item.hours)} hrs`,
        }));
      }
      if (!record?.details && !record?.consultantRecords) {
        return [{ period: '—', hoursLabel: '—' }];
      }
      const total = record?.consultantHoursTotal != null
        ? this.formatHoursValue(record.consultantHoursTotal)
        : '0';
      return [{ period: this.periodLabel(record), hoursLabel: `${total} hrs` }];
    },
    periodLabel(record) {
      if (record?.loadingDetails && !this.consultantRecordsFor(record).length) return '…';
      const periods = [];
      const seen = new Set();
      this.consultantRecordsFor(record).forEach((row) => {
        const period = this.periodFromRow(row);
        if (!period) return;
        const key = period.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        periods.push(period);
      });
      if (!periods.length) {
        if (!record?.details && !record?.consultantRecords) return '—';
        return 'Not set';
      }
      return periods.join(', ');
    },
    consultantNamesLabel(record) {
      if (record?.loadingDetails && !this.consultantRecordsFor(record).length) return '…';
      const names = [];
      const seen = new Set();
      this.consultantRecordsFor(record).forEach((row) => {
        const name = String(row.consultantName || row.name || '').trim();
        if (!name) return;
        const key = name.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        names.push(name);
      });
      if (!names.length) {
        if (!record?.details && !record?.consultantRecords) return '—';
        return '—';
      }
      return names.join(', ');
    },
    rateLabel(record) {
      if (record?.loadingDetails && !this.consultantRecordsFor(record).length) return '…';
      const rates = [];
      const seen = new Set();
      this.consultantRecordsFor(record).forEach((row) => {
        const rate = Number(row.hourlyRate);
        if (!Number.isFinite(rate) || rate < 0) return;
        const key = String(rate);
        if (seen.has(key)) return;
        seen.add(key);
        rates.push(`${this.formatCurrency(rate)}/hr`);
      });
      if (!rates.length) {
        if (!record?.details && !record?.consultantRecords) return '—';
        return '—';
      }
      return rates.join(', ');
    },
    vendorInitials(name) {
      const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) return 'VB';
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    },
    vendorLogoForRecord(record) {
      const vendorId = record?.vendorId;
      if (vendorId == null) return '';
      const cached = this.vendorLogoUrls[String(vendorId)];
      return typeof cached === 'string' && cached ? cached : '';
    },
    onVendorLogoError(record) {
      const key = record?.vendorId != null ? String(record.vendorId) : '';
      if (!key) return;
      const url = this.vendorLogoUrls[key];
      if (url && String(url).startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
      this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: null };
    },
    revokeAllVendorLogoUrls() {
      Object.values(this.vendorLogoUrls || {}).forEach((url) => {
        if (url && String(url).startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      this.vendorLogoUrls = {};
    },
    async prefetchVendorLogosForRecords(records = []) {
      const ids = [
        ...new Set(
          (records || [])
            .map((r) => r?.vendorId)
            .filter((id) => id != null)
            .map((id) => String(id))
        ),
      ];
      if (!ids.length) return;
      await Promise.all(ids.map((id) => this.ensureVendorLogoLoaded(id)));
    },
    async ensureVendorLogoLoaded(vendorId) {
      const key = String(vendorId);
      if (Object.prototype.hasOwnProperty.call(this.vendorLogoUrls, key)) return;
      if (!this.companyId || !key) {
        this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: null };
        return;
      }

      this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: undefined };

      try {
        const response = await ApiService.get(
          `/vendor/vendors/${vendorId}/portal-logo?companyId=${encodeURIComponent(this.companyId)}`,
          { responseType: 'blob' }
        );
        const blob = response.data;
        const headerType = String(
          response.headers?.['content-type']
          || response.headers?.['Content-Type']
          || ''
        ).toLowerCase();
        const blobType = String(blob?.type || '').toLowerCase();
        const looksLikeImage = Boolean(
          blob instanceof Blob
          && blob.size > 0
          && !headerType.includes('json')
          && !blobType.includes('json')
          && (
            headerType.startsWith('image/')
            || blobType.startsWith('image/')
            || headerType.includes('octet-stream')
            || (!headerType && !blobType)
          )
        );
        if (!looksLikeImage) {
          this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: null };
          return;
        }
        const mime = blobType.startsWith('image/')
          ? blobType
          : (headerType.startsWith('image/') ? headerType : 'image/jpeg');
        const url = URL.createObjectURL(
          blobType.startsWith('image/') ? blob : new Blob([blob], { type: mime })
        );
        this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: url };
      } catch (error) {
        logger.warn('Could not load vendor portal logo:', vendorId, error);
        this.vendorLogoUrls = { ...this.vendorLogoUrls, [key]: null };
      }
    },
    async fetchVendors() {
      if (!this.companyId) return;
      try {
        const [vendors, termsRes] = await Promise.all([
          ApiService.getAllVendors(this.companyId),
          ApiService.getAllPaymentTerms(this.companyId).catch(() => null),
        ]);
        this.vendors = Array.isArray(vendors) ? vendors : [];
        const termsPayload = termsRes?.paymentTerms || termsRes?.data?.paymentTerms || termsRes;
        this.paymentTerms = Array.isArray(termsPayload) ? termsPayload : [];
        await this.prefetchVendorLogosForRecords(this.paginatedRecords);
      } catch (error) {
        logger.error('Error fetching vendors:', error);
        this.vendors = [];
        this.paymentTerms = [];
      }
    },
    async fetchData() {
      if (!this.companyId) return;
      this.loading = true;
      try {
        const filters = { view: 'All', dateFilter: 'All' };
        if (this.selectedVendorId && this.selectedVendorId !== 'All') {
          filters.vendorId = this.selectedVendorId;
        }
        const data = await ApiService.getBillsAndExpenses(this.companyId, filters);
        this.allRecords = Array.isArray(data)
          ? data.map((record) => ({
              ...record,
              showDetails: true,
              loadingDetails: false,
              details: null,
            }))
          : [];
      } catch (error) {
        logger.error('Error fetching draft bills:', error);
        this.allRecords = [];
      } finally {
        this.loading = false;
      }
    },
    async loadDetailsForRecords(list = []) {
      const pending = (list || []).filter((record) => record && !record.details && !record.loadingDetails);
      if (!pending.length) return;

      await Promise.all(
        pending.map(async (record) => {
          record.loadingDetails = true;
          try {
            await ensureRecordDetails(record);
          } catch (error) {
            logger.error('Error fetching bill details:', error);
            record.details = { accountRecords: [], itemRecords: [], consultantRecords: [] };
          } finally {
            record.loadingDetails = false;
          }
        })
      );
    },
    formatDate(date) {
      return formatUsDate(date);
    },
    formatCurrency(amount) {
      return formatPanelCurrency(amount);
    },
  },
};
</script>
